/**
 * Why `POST /auth/token` refused to open a session
 * (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md, *The session
 * is bound*, TECH-3354).
 *
 * Two codes, on purpose. The record asks that a token naming no GCIP tenant be
 * refused with an error SAYING SO, because that failure has one cause the
 * person can act on: they signed in somewhere other than their Organisation's
 * own address (the desktop login, a session restored from before identity
 * homes). Every other refusal — a tenant this Instance does not know, a
 * switched-off identity home, an account with no User inside the home — is one
 * code and one message, because telling them apart would let a token minted
 * elsewhere probe which Organisations exist and who belongs to them, which is
 * the record's *No probing*.
 *
 * Two later codes say why as well, and reveal nothing for the same reason: only
 * the holder of a token that was valid for this account can be told them.
 * `CROSS_INSTANCE_SESSION_ENDED` is a Cross-Instance sign-in past its maximum
 * age. `SESSION_REVOKED` is a sign-in that has ended since its token was made:
 * revoked by a password reset, or its account disabled (TECH-3497).
 *
 * Shared so the client reacts to the CODE rather than to a sentence: a refused
 * session is signed out of, and a message is not a contract.
 */
export const SESSION_TOKEN_REFUSAL_CODES = [
	"IDENTITY_HOME_REQUIRED",
	"IDENTITY_HOME_REFUSED",
	"CROSS_INSTANCE_SESSION_ENDED",
	"SESSION_REVOKED",
] as const;

export type SessionTokenRefusalCode =
	(typeof SESSION_TOKEN_REFUSAL_CODES)[number];

export const isSessionTokenRefusalCode = (
	value: unknown
): value is SessionTokenRefusalCode =>
	typeof value === "string" &&
	(SESSION_TOKEN_REFUSAL_CODES as readonly string[]).includes(value);

/** The close code a connection gets when its Cross-Instance session reaches its maximum age. */
export const CROSS_INSTANCE_SESSION_ENDED_CLOSE_CODE = 4401;
